import React, { useMemo, useRef, useState, useEffect } from "react";
import { Plus, Trash2, Settings, Eye, Download, Upload, RefreshCw } from "lucide-react";

import { useStudentAssignments } from "./useStudentAssignments";
import Spinner from "@/components/common/spinner/Spinner";
import AlertMessage from "@/components/layout/message/AlertMessage";
import Tabs from "@/components/common/tab/Tab";
import ManagementHeader from "../admin/managementHeader/ManagementHeader";
import { SimulationTable } from "../simulationManagement/components/simulationTable/SimulationTable";
import PaginationControls from "@/components/layout/paginationControls/PaginationControls";
import ActionPanel from "@/components/layout/actionPanel/ActionPanel";
import styles from "./StudentAssignment.module.css";
import type { simulation } from "../dashboard/types.api";

const PAGE_SIZE = 10;

const StudentAssignments: React.FC = () => {
    useEffect(() => {
        document.body.classList.add("solid-public-bg");
        return () => document.body.classList.remove("solid-public-bg");
    }, []);

    const {
        state,
        assignedPage,
        setAssignedPage,
        availablePage,
        setAvailablePage,
        availableFilters,
        setAvailableFilters,
        handleAssign,
        handleUnassign,
        handleReset,
        handleTabChange,
        handleExport,
        handleConfigureAnswers,
        handleViewDashboard,
        handleFileUpload,
        userId,
    } = useStudentAssignments();

    const {
        student,
        isLoading,
        error,
        actionError,
        activeTab,
        available: availableSimulations,
        assigned,
        exportingId,
        uploadingSimId,
    } = state;

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploadTargetSim, setUploadTargetSim] = useState<simulation | null>(
        null,
    );

    const onFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && uploadTargetSim) {
            handleFileUpload(uploadTargetSim, file);
        }
        if (event.target) {
            event.target.value = "";
        }
    };

    const assignedActions = useMemo(
        () => [
            {
                label: "Gestionar Respuestas",
                handler: handleConfigureAnswers,
                icon: <Settings size={16} />,
                variant: "secondary" as const,
            },
            {
                label: "Ver Dashboard",
                handler: handleViewDashboard,
                icon: <Eye size={16} />,
                variant: "primary" as const,
            },
            {
                label: "Exportar Reporte",
                handler: handleExport,
                icon: <Download size={16} />,
                isLoading: (sim: simulation) => exportingId === sim.id,
                variant: "secondary" as const,
            },
            {
                label: "Importar Respuestas",
                handler: (sim: simulation) => {
                    setUploadTargetSim(sim);
                    fileInputRef.current?.click();
                },
                icon: <Upload size={16} />,
                isLoading: (sim: simulation) => uploadingSimId === sim.id,
                variant: "secondary" as const,
                disabled: (sim: simulation) => sim.state !== "published",
                tooltip: (sim: simulation) =>
                    sim.state !== "published"
                        ? "Solo se pueden importar respuestas para simulacros 'Publicados'"
                        : "Importar respuestas desde archivo",
            },
            {
                label: "Reiniciar Intento",
                handler: handleReset,
                icon: <RefreshCw size={16} />,
                variant: "secondary" as const,
                tooltip: "Borra el progreso y las respuestas del estudiante para que pueda volver a presentar el simulacro.",
                disabled: (sim: simulation) => sim.participation_status === 'not_started',
            },

            {
                label: "Quitar Asignación",
                handler: handleUnassign,
                icon: <Trash2 size={16} />,
                variant: "danger" as const,
            },
        ],
        [
            handleUnassign,
            handleReset,
            handleExport,
            handleConfigureAnswers,
            handleViewDashboard,
            handleFileUpload,
            exportingId,
            uploadingSimId,
        ],
    );

    const availableActions = useMemo(
        () => [
            {
                label: "Asignar",
                handler: handleAssign,
                icon: <Plus size={16} />,
                variant: "primary" as const,
            },
        ],
        [handleAssign],
    );

    if (isLoading || !student) {
        return error ? (
            <div className={styles["container-main"]}>
                <ManagementHeader linkTo="/facilitator-panel/user-management" />
                <AlertMessage type="error" title="Error">
                    {error.generalMessage}
                </AlertMessage>
            </div>
        ) : (
            <Spinner text="Cargando datos del estudiante..." />
        );
    }

    return (
        <div className={styles["container-main"]}>
            <input
                type="file"
                ref={fileInputRef}
                onChange={onFileSelected}
                accept=".csv"
                style={{ display: "none" }}
            />
            <ManagementHeader linkTo="/facilitator-panel/user-management" />
            <h1 className={styles["container-title"]}>
                Asignaciones de:{" "}
                {student
                    ? `${student.first_name} ${student.last_name}`
                    : `Usuario #${userId}`}
            </h1>
            <AlertMessage type="error" title="Error">
                {error?.generalMessage || actionError?.generalMessage}
            </AlertMessage>

            <Tabs
                items={[
                    {
                        key: "assigned",
                        label: assigned.count
                            ? `Asignados (${assigned.count})`
                            : "Asignados (0)",
                    },
                    { key: "available", label: "Disponibles para Asignar" },
                ]}
                activeKey={activeTab}
                onTabChange={(key) => handleTabChange(key as "assigned" | "available")}
            />

            <div className={styles["tab-content"]}>
                {activeTab === "assigned" ? (
                    <>
                        <SimulationTable
                            data={assigned.results}
                            isLoading={isLoading}
                            actions={assignedActions}
                        />
                        <PaginationControls
                            currentPage={assignedPage}
                            totalCount={assigned.count}
                            pageSize={PAGE_SIZE}
                            onPageChange={setAssignedPage}
                        />
                    </>
                ) : (
                    <>
                        <ActionPanel
                            placeholder="Buscar simulacros disponibles..."
                            searchTerm={availableFilters.search}
                            onSearchChange={(search) => setAvailableFilters({ search })}
                        />
                        <SimulationTable
                            data={availableSimulations.results}
                            isLoading={isLoading}
                            actions={availableActions}
                        />
                        <PaginationControls
                            currentPage={availablePage}
                            totalCount={availableSimulations.count}
                            pageSize={PAGE_SIZE}
                            onPageChange={setAvailablePage}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default StudentAssignments;
